import React from 'react';
import {Navigate, RouteObject, useParams} from 'react-router';
import {authGuard} from '@common/auth/guards/auth-route';
import {TR_TO_TARGET, type SegmentMap} from '@app/i18n/route-translations';
import type {AppLocale} from '@app/i18n/locale-config';

// K2 fix (5 Tem gece denetimi) — /{locale}/giris|kayit|login|register URL'leri
// SPA'da catch-all ChannelPage'e düşüp BOŞ "İçerikler" sayfası render ediyordu
// (giriş formu yalnız prefix'siz /giris'te tanımlı — auth-routes.tsx). Locale
// çocuklarına eklenen bu redirect'ler kullanıcıyı çalışan köke devreder.
function AuthPathRedirect({to}: {to: string}) {
  return <Navigate to={to} replace />;
}

// K1/P6 — TR base /tr/izle/{id} kanonik prefix'siz oynatıcıya (/izle/{id})
// devredilir. NON-TR localized izleme URL'leri (/es/ver/{id}, /en/watch/{id},
// /ar/mushahada/{id}, /ru/smotret/{id}) ise ARTIK WatchPage'i YERİNDE render
// eder (localizeChildren içinde özel-kılıf) — P6: hedef-dilli video on-site +
// doğru dilde açılsın diye. Bu yüzden bu redirect sadece TR-prefix'li base'e
// uygulanır.
function LocalizedWatchRedirect() {
  const {videoId} = useParams();
  return <Navigate to={`/izle/${videoId}`} replace />;
}

const lazyRoute = async (
  cmp: keyof typeof import('@app/routes/site-routes.lazy'),
) => {
  const exports = await import('@app/routes/site-routes.lazy');
  return {
    Component: exports[cmp],
  };
};

// BLOK 5 — Profil sekme panelleri (Outlet child'ları). Hem prefix'siz kök
// rota hem de /tr (ve türetilen /en|/es|/ar|/ru) profil rotası AYNI seti
// kullansın diye tek yerde tanımlı (kopya sürüklenmesini önler).
const profileTabChildren: RouteObject[] = [
  {index: true, lazy: () => lazyRoute('ProfileListsPanel')},
  {path: 'lists', lazy: () => lazyRoute('ProfileListsPanel')},
  {path: 'ratings', lazy: () => lazyRoute('ProfileRatingsPanel')},
  {path: 'reviews', lazy: () => lazyRoute('ProfileReviewsPanel')},
  {path: 'comments', lazy: () => lazyRoute('ProfileCommentsPanel')},
  {path: 'followers', lazy: () => lazyRoute('ProfileFollowersPanel')},
  {path: 'followed-users', lazy: () => lazyRoute('ProfileFollowedUsersPanel')},
];

// Turkish locale route children
const trLocaleChildren: RouteObject[] = [
  // Homepage  /tr/
  {
    index: true,
    lazy: () => lazyRoute('Homepage'),
  },

  // Films  /tr/film/{slug}
  // D38 (Plan v9.3 E4 Faz 1) — yeni Cine5FilmDetailPage (referans
  // 02-film-detay.png pixel-perfect). Eski TitlePage dosyaları
  // silinmedi; alt route'lar (/videolar /gorseller /tam-kadro) hâlâ
  // eski TitlePage variantlarına bağlı (D39+ ileride tek tek geçer).
  // Dizi slug'ı /tr/film/...'a düşerse Cine5FilmDetailPage içeride
  // <Navigate to={getTitleLink(title)}> ile /tr/dizi/...'a redirect.
  {path: 'film/:slug', lazy: () => lazyRoute('Cine5FilmDetailPage')},
  {path: 'film/:slug/videolar', lazy: () => lazyRoute('TitleVideosPage')},
  {path: 'film/:slug/gorseller', lazy: () => lazyRoute('TitleImagesPage')},
  {path: 'film/:slug/tam-kadro', lazy: () => lazyRoute('TitleFullCreditsPage')},

  // Series  /tr/dizi/{slug}
  // D39 (Plan v9.3 E4 Faz 1) — yeni Cine5SeriesDetailPage (referans
  // 03-dizi-detay.jpeg pixel-perfect). Eski TitlePage + sezon/bölüm alt
  // route'ları SİLİNMEDİ (graceful fallback). Film slug'ı /tr/dizi/...'a
  // düşerse Cine5SeriesDetailPage içeride /tr/film/...'a redirect eder.
  {path: 'dizi/:slug', lazy: () => lazyRoute('Cine5SeriesDetailPage')},
  {path: 'dizi/:slug/videolar', lazy: () => lazyRoute('TitleVideosPage')},
  {path: 'dizi/:slug/gorseller', lazy: () => lazyRoute('TitleImagesPage')},
  {path: 'dizi/:slug/tam-kadro', lazy: () => lazyRoute('TitleFullCreditsPage')},
  {path: 'dizi/:slug/sezon/:season', lazy: () => lazyRoute('SeasonPage')},
  {
    path: 'dizi/:slug/sezon/:season/bolum/:episode',
    lazy: () => lazyRoute('EpisodePage'),
  },
  {
    path: 'dizi/:slug/sezon/:season/bolum/:episode/tam-kadro',
    lazy: () => lazyRoute('EpisodeFullCreditsPage'),
  },
  {
    path: 'dizi/:slug/sezon/:season/bolum/:episode/videolar',
    lazy: () => lazyRoute('EpisodeVideosPage'),
  },

  // Person  /tr/kisi/{slug}
  // D40 (Plan v9.3 E4 Faz 1) — yeni pixel-perfect Cine5PersonDetailPage
  // (referans 04-oyuncu-detay.jpeg). Eski PersonPage legacy /oyuncu/:id
  // route'larında graceful fallback olarak korunur.
  {path: 'kisi/:slug', lazy: () => lazyRoute('Cine5PersonDetailPage')},

  // Genre channel  /tr/tur/{genreName}
  // Channel slug is 'genre', the URL segment is the restriction (genre name)
  {path: 'tur/:restriction', lazy: () => lazyRoute('GenreChannelPage')},

  // Keyword channel  /tr/anahtar-kelime/{keywordName}
  // Channel slug is 'keyword', the URL segment is the restriction (keyword name)
  {path: 'anahtar-kelime/:restriction', lazy: () => lazyRoute('KeywordChannelPage')},

  // Country channel  /tr/ulkeler/{trSlug}  (localized: /en/countries/..., /es/paises/...)
  // Channel slug is 'ulkeler'; URL segment translated per-locale by localizeChildren.
  // restriction is the Turkish slug (almanya); backend maps TR slug → ISO.
  {path: 'ulkeler/:restriction', lazy: () => lazyRoute('CountryChannelPage')},

  // Year pages  /tr/yil/2024
  {path: 'yil/:year', lazy: () => lazyRoute('YearPage')},
  {path: 'yil/:year/:type', lazy: () => lazyRoute('YearPage')},

  // Platform hub  /tr/platformlar — D42: 404 → 200 FIX (Cine5PlatformHubPage).
  // Catch-all (:slugOrId) ÖNCESİNDE tanımlı olmalı (aksi halde kanal sanılır).
  {path: 'platformlar', lazy: () => lazyRoute('Cine5PlatformHubPage')},

  // Platform pages  /tr/platform/netflix
  {path: 'platform/:platformSlug', lazy: () => lazyRoute('PlatformPage')},

  // Search  /tr/ara
  {path: 'ara', lazy: () => lazyRoute('SearchPage')},
  {path: 'ara/:query', lazy: () => lazyRoute('SearchPage')},

  // News hub  /tr/haberler — D41 pixel-perfect Cine5HaberlerPage (route swap).
  {path: 'haberler', lazy: () => lazyRoute('Cine5HaberlerPage')},

  // News article  /tr/haber/{slug}
  {path: 'haber/:articleId', lazy: () => lazyRoute('NewsArticlePage')},

  // Listing pages — Turkish path → English channel slug
  {path: 'filmler', lazy: () => lazyRoute('MoviesChannelPage')},
  {path: 'diziler', lazy: () => lazyRoute('SeriesChannelPage')},
  {path: 'oyuncular', lazy: () => lazyRoute('PeopleChannelPage')},

  // BLOK 3a: İzleme Listem — header'daki bookmark buraya bağlanır. Catch-all
  // (:slugOrId) ÖNCESİNDE olmalı, aksi halde kanal sanılıp 404 verirdi.
  // localizeChildren TR fallback seti üzerinden /en|/es|/ar|/ru için de çözer.
  {
    path: 'izleme-listesi',
    loader: () => authGuard(),
    lazy: () => lazyRoute('WatchlistPage'),
  },

  // BLOK 3b: Ücretsiz İzle listeleme sayfası (admin "Ücretsiz İzle" flag'li +
  // primary_video'lu başlıklar). Catch-all ÖNCESİNDE.
  {path: 'ucretsiz-izle', lazy: () => lazyRoute('FreeWatchPage')},

  // PAKET 15.1 — Kariyer başvuru sayfası (Kafkas Bey talebi). Catch-all
  // ÖNCESİNDE olmalı, aksi halde kanal sanılıp 404 verir. localizeChildren
  // TR fallback'iyle /en|/es|/ar|/ru altında da çözülür.
  {path: 'kariyer', lazy: () => lazyRoute('KariyerPage')},

  // K1 (5 Tem) — locale-prefix'li izleme URL'leri prefix'siz kanonik oynatıcıya
  // devredilir (izle→watch/ver/mushahada/smotret çevirisini localizeChildren
  // otomatik üretir). Catch-all ÖNCESİNDE olmalı.
  {path: 'izle/:videoId', element: <LocalizedWatchRedirect />},

  // K2 (5 Tem) — locale-prefix'li auth URL'leri çalışan prefix'siz auth
  // route'larına devredilir (aksi halde catch-all boş kanal sayfası açıyordu).
  {path: 'giris', element: <AuthPathRedirect to="/giris" />},
  {path: 'kayit', element: <AuthPathRedirect to="/kayit" />},
  {path: 'login', element: <AuthPathRedirect to="/giris" />},
  {path: 'register', element: <AuthPathRedirect to="/kayit" />},
  {path: 'sifremi-unuttum', element: <AuthPathRedirect to="/sifremi-unuttum" />},

  // BLOK 5 — Profil + liste yönetim sayfaları /tr altında da çözülsün.
  // KÖK SEBEP: bu rotalar SADECE kök (prefix'siz) seviyede tanımlıydı; ama
  // SetLocale prefix'siz URL'yi /tr'ye 302'liyor → /tr/kullanici/... hiçbir
  // client route'a uymuyordu → direct-load/refresh 404. (In-app client nav
  // kök route'u yakaladığı için sadece uygulama içi çalışıyordu — bu yüzden
  // header avatar /tr/kullanici/:id de kanal sanılıp bozuktu.) Catch-all
  // ÖNCESİNDE. localizeChildren 'kullanici'→'user' çevirir → /en/user/... da
  // otomatik çözülür (nested panel child'ları spread ile korunur).
  {
    // Header avatarı slug'sız /tr/kullanici/:id linkliyor — id-only rota da
    // aynı panel setini alsın (aksi halde Outlet boş kalırdı).
    path: 'kullanici/:userId',
    lazy: () => lazyRoute('UserProfilePage'),
    children: profileTabChildren,
  },
  {
    path: 'kullanici/:userId/:slug',
    lazy: () => lazyRoute('UserProfilePage'),
    children: profileTabChildren,
  },
  {
    path: 'kullanici/:userId/:slug/:tab',
    lazy: () => lazyRoute('UserProfilePage'),
  },
  {
    path: 'listeler',
    loader: () => authGuard(),
    lazy: () => lazyRoute('UserListsIndexPage'),
  },
  {
    path: 'listeler/yeni',
    loader: () => authGuard(),
    lazy: () => lazyRoute('SiteCreateUserListPage'),
  },
  {path: 'listeler/:slugOrId', lazy: () => lazyRoute('ListChannelPage')},
  {
    path: 'listeler/:slugOrId/duzenle',
    loader: () => authGuard(),
    lazy: () => lazyRoute('SiteEditUserListPage'),
  },

  // Channel catch-all for any other channel page under /tr/
  {path: ':slugOrId', lazy: () => lazyRoute('ChannelPage')},
  {path: ':slugOrId/:restriction', lazy: () => lazyRoute('ChannelPage')},
];

// ─────────────────────────────────────────────────────────────────────────
// PER-LOCALE CHILDREN FACTORY (Plan v7 · Day 25 F8c)
//
// Each non-TR locale gets its own RouteObject[] where every TR path
// segment is replaced with the locale's canonical (TR_TO_TARGET map).
// The locale-canonical paths are merged BEFORE the TR fallback paths
// so React Router prefers the canonical match — but the TR variants
// remain reachable, mirroring the backend's hub-spoke behaviour.
//
// Examples (en):
//   /en/movie/:slug      -> TitlePage  (canonical, primary)
//   /en/film/:slug       -> TitlePage  (TR fallback, secondary)
//   /en/dizi/:slug       -> TitlePage  (TR fallback)
//   /en/tv-show/:slug    -> TitlePage  (canonical)
//
// AR/RU canonical 'film' segment matches TR exactly, so no extra route
// is generated for those (translatePath returns identical string).
// ─────────────────────────────────────────────────────────────────────────

function translatePath(path: string, segMap: SegmentMap): string {
  // Replace every "/{trSegment}/" with "/{localizedSegment}/", leaving
  // route params (':slug') and dynamic segments untouched.
  return path
    .split('/')
    .map(part => (part.startsWith(':') || part === '' ? part : segMap[part] ?? part))
    .join('/');
}

function localizeChildren(locale: Exclude<AppLocale, 'tr'>): RouteObject[] {
  const segMap = TR_TO_TARGET[locale];
  const localized: RouteObject[] = [];
  for (const child of trLocaleChildren) {
    if (!child.path) {
      // index route — every locale homepage shares this
      localized.push(child);
      continue;
    }
    const newPath = translatePath(child.path, segMap);
    if (newPath !== child.path) {
      // P6 — locale-prefix'li izleme route'u artık REDIRECT değil, WatchPage'i
      // YERİNDE render eder: /es/ver/:id, /en/watch/:id, /ar/mushahada/:id,
      // /ru/smotret/:id. Böylece hedef-dilli video on-site + doğru dilde açılır
      // (YouTube'a ya da Türkçe sayfaya düşmez). TR base (/tr/izle/:id) hâlâ
      // prefix'siz kanoniğe (LocalizedWatchRedirect) devreder.
      if (child.path === 'izle/:videoId') {
        localized.push({path: newPath, lazy: () => lazyRoute('WatchPage')});
      } else {
        localized.push({...child, path: newPath});
      }
    }
  }
  // Canonical first, TR fallback after so backend's 200-on-TR responses
  // continue to match a React Router route (otherwise catch-all would
  // hijack and render the wrong page).
  return [...localized, ...trLocaleChildren];
}

const enLocaleChildren = localizeChildren('en');
const arLocaleChildren = localizeChildren('ar');
const esLocaleChildren = localizeChildren('es');
const ruLocaleChildren = localizeChildren('ru');

export const siteRoutes: RouteObject[] = [
  {
    loader: () => authGuard({permission: 'titles.view', requireLogin: false}),
    children: [
      // Root  /
      {
        index: true,
        lazy: () => lazyRoute('Homepage'),
      },

      // ─────────────────────────────────────────────────────────────────────
      // LOCALE-PREFIXED CONTENT ROUTES (Plan v7 · Day 25 F8c)
      //
      // Each locale gets canonical-segment-aware children (movie / pelicula
      // / musalsal / ...) plus a TR fallback set so legacy share URLs and
      // any /xx/film/... requests still resolve to the right component.
      // The TR locale uses trLocaleChildren directly (no transformation).
      // ─────────────────────────────────────────────────────────────────────
      {path: 'tr', children: trLocaleChildren},
      {path: 'en', children: enLocaleChildren},
      {path: 'ar', children: arLocaleChildren},
      {path: 'es', children: esLocaleChildren},
      {path: 'ru', children: ruLocaleChildren},

      // ─────────────────────────────────────────────────────────────────────
      // LEGACY ROUTES (no locale prefix)
      // ─────────────────────────────────────────────────────────────────────
      {path: 'anasayfa', lazy: () => lazyRoute('LandingPage')},
      {path: 'landing', lazy: () => lazyRoute('LandingPage')},

      // Watchlist without locale prefix (B2): the auth-dropdown custom menu
      // links to a bare "/izleme-listesi"; resolve it client-side here too.
      {
        path: 'izleme-listesi',
        loader: () => authGuard(),
        lazy: () => lazyRoute('WatchlistPage'),
      },

      // Old icerik routes — R3 (5 Tem): eski MTDB sayfaları yerine server 301
      // zincirine devreden redirect (SPA'da eski tasarım render edilmez;
      // server slug üretir + yeni locale-aware URL'e kalıcı yönlendirir).
      {path: 'icerik/:titleId/:titleSlug', lazy: () => lazyRoute('LegacyIcerikRedirect')},
      {path: 'icerik/:titleId/:titleSlug/videos', lazy: () => lazyRoute('LegacyIcerikRedirect')},
      {path: 'icerik/:titleId/:titleSlug/images', lazy: () => lazyRoute('LegacyIcerikRedirect')},
      {path: 'icerik/:titleId/:titleSlug/full-credits', lazy: () => lazyRoute('LegacyIcerikRedirect')},
      {path: 'icerik/:titleId/:titleSlug/sezon/:season', lazy: () => lazyRoute('LegacyIcerikRedirect')},
      {path: 'icerik/:titleId/:titleSlug/sezon/:season/bolum/:episode', lazy: () => lazyRoute('LegacyIcerikRedirect')},

      // Old oyuncu routes
      {path: 'oyuncu/:personId', lazy: () => lazyRoute('PersonPage')},
      {path: 'oyuncu/:personId/:personSlug', lazy: () => lazyRoute('PersonPage')},

      // Watch (no locale prefix)
      {path: 'izle/:videoId', lazy: () => lazyRoute('WatchPage')},
      {path: 'watch/:videoId', lazy: () => lazyRoute('WatchPage')},

      // Profile & user pages
      {
        path: 'kullanici/:userId/:slug',
        lazy: () => lazyRoute('UserProfilePage'),
        children: [
          {index: true, lazy: () => lazyRoute('ProfileListsPanel')},
          {path: 'lists', lazy: () => lazyRoute('ProfileListsPanel')},
          {path: 'ratings', lazy: () => lazyRoute('ProfileRatingsPanel')},
          {path: 'reviews', lazy: () => lazyRoute('ProfileReviewsPanel')},
          {path: 'comments', lazy: () => lazyRoute('ProfileCommentsPanel')},
          {path: 'followers', lazy: () => lazyRoute('ProfileFollowersPanel')},
          {path: 'followed-users', lazy: () => lazyRoute('ProfileFollowedUsersPanel')},
        ],
      },
      {path: 'kullanici/:userId/:slug/:tab', lazy: () => lazyRoute('UserProfilePage')},
      {path: 'user/:userId/:slug', lazy: () => lazyRoute('UserProfilePage')},
      {path: 'user/:userId/:slug/:tab', lazy: () => lazyRoute('UserProfilePage')},

      // Watchlist (B2): kullanıcıya özel izleme listesi
      {
        path: 'izleme-listesi',
        loader: () => authGuard(),
        lazy: () => lazyRoute('WatchlistPage'),
      },

      // User Lists
      {
        path: 'listeler',
        loader: () => authGuard(),
        lazy: () => lazyRoute('UserListsIndexPage'),
      },
      {
        path: 'listeler/yeni',
        loader: () => authGuard(),
        lazy: () => lazyRoute('SiteCreateUserListPage'),
      },
      {
        path: 'listeler/new',
        loader: () => authGuard(),
        lazy: () => lazyRoute('SiteCreateUserListPage'),
      },
      {path: 'listeler/:slugOrId', lazy: () => lazyRoute('ListChannelPage')},
      {
        path: 'listeler/:slugOrId/duzenle',
        loader: () => authGuard(),
        lazy: () => lazyRoute('SiteEditUserListPage'),
      },

      // Channels (direct slug access without locale)
      {path: 'kanal/:slugOrId', lazy: () => lazyRoute('ChannelPage')},
      {path: 'kanal/:slugOrId/:restriction', lazy: () => lazyRoute('ChannelPage')},
      {path: ':slugOrId', lazy: () => lazyRoute('ChannelPage')},
      {path: ':slugOrId/:restriction', lazy: () => lazyRoute('ChannelPage')},
    ],
  },
];
